/**
 * Camera configuration definitions.
 * Separates player-editable settings from developer-only settings and constants.
 */

import { ConfigCategory, ConfigVisibility, ConfigSchema } from '../types';

/**
 * Camera configuration values.
 * This is the runtime configuration that can be modified.
 */
export interface CameraConfig {
  // Player-editable settings
  defaultZoom: number;
  panSpeed: number;
  edgeScrollZone: number;
  edgeScrollSpeed: number;

  // Developer-only settings (not visible to players)
  minZoom: number;
  maxZoom: number;
  enableEdgeScrolling: boolean; // Dev toggle for testing
  enableKeyboardPanning: boolean; // Dev toggle for testing
}

/**
 * Default camera configuration values.
 */
// Camera angle is hardcoded to 45 degrees (isometric view)
export const CAMERA_ANGLE = 45;

export const DEFAULT_CAMERA_CONFIG: CameraConfig = {
  // Player settings with sensible defaults
  defaultZoom: 1.5,
  minZoom: 0.5,
  maxZoom: 2.0,
  panSpeed: 12,
  edgeScrollZone: 50,
  edgeScrollSpeed: 14,

  // Developer settings
  enableEdgeScrolling: true,
  enableKeyboardPanning: true,
};

/**
 * Camera configuration schema with metadata.
 * Defines which settings are visible/editable by whom.
 */
export const CAMERA_CONFIG_SCHEMA: ConfigSchema<CameraConfig> = {
  defaultZoom: {
    label: 'Default Zoom',
    description: 'Initial camera zoom level when starting the game',
    category: ConfigCategory.CAMERA,
    visibility: ConfigVisibility.PLAYER,
    min: 0.5,
    max: 3.0,
    step: 0.1,
  },
  minZoom: {
    label: 'Minimum Zoom',
    description: 'How far out you can zoom the camera (dev only)',
    category: ConfigCategory.CAMERA,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 1.0,
    step: 0.1,
  },
  maxZoom: {
    label: 'Maximum Zoom',
    description: 'How far in you can zoom the camera (dev only)',
    category: ConfigCategory.CAMERA,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1.0,
    max: 5.0,
    step: 0.1,
  },
  panSpeed: {
    label: 'Pan Speed',
    description: 'Speed of camera movement when using keyboard or edge scrolling',
    category: ConfigCategory.CAMERA,
    visibility: ConfigVisibility.PLAYER,
    min: 1,
    max: 50,
    step: 1,
  },
  edgeScrollZone: {
    label: 'Edge Scroll Zone',
    description: 'Distance from screen edge that triggers edge scrolling (in pixels)',
    category: ConfigCategory.CAMERA,
    visibility: ConfigVisibility.PLAYER,
    min: 0,
    max: 200,
    step: 5,
  },
  edgeScrollSpeed: {
    label: 'Edge Scroll Speed',
    description: 'Speed of camera movement when edge scrolling',
    category: ConfigCategory.CAMERA,
    visibility: ConfigVisibility.PLAYER,
    min: 1,
    max: 50,
    step: 1,
  },
  enableEdgeScrolling: {
    label: 'Enable Edge Scrolling',
    description: 'Toggle edge scrolling feature (for testing)',
    category: ConfigCategory.DEBUG,
    visibility: ConfigVisibility.DEVELOPER,
  },
  enableKeyboardPanning: {
    label: 'Enable Keyboard Panning',
    description: 'Toggle keyboard panning feature (for testing)',
    category: ConfigCategory.DEBUG,
    visibility: ConfigVisibility.DEVELOPER,
  },
};
